import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { EventListItem } from '../types/events.types';

import { getRouteForEventType } from 'routes/referral/hooks/useEventTypeRedirect';
import { setShowCurrencyLotteryModal, setShowDailyRewardsModal } from 'actions/global-actions';

import { eventFormatDate, eventFormatParticipantCount } from '../utils';
import { CustomGlassBox } from './CustomGlassBox';
import styles from './EventCard.module.scss';
import { RewardsList } from './RewardsList';
import { StatusBadge } from './StatusBadge';

type EventCardProps = {
  event: EventListItem;
};

export const EventCard = ({ event }: EventCardProps) => {
  const dispatch = useDispatch();
  const [imageError, setImageError] = useState(false);
  const hasImage = !!event.image_url && !imageError;
  const hasParticipantCount = event.participant_count > 0;
  const hasVipBadge = event.event_label === 'vip';
  const isCustomEvent = !!event._customAction;

  const handleCustomEventClick = () => {
    if (event._customAction === 'wheel-of-fortune') {
      dispatch(setShowCurrencyLotteryModal(true));
    } else if (event._customAction === 'daily-rewards') {
      const eventDate = new Date(event.start_datetime);
      dispatch(setShowDailyRewardsModal({ year: eventDate.getFullYear(), month: eventDate.getMonth() + 1 }));
    }
  };

  const cardContent = (
    <CustomGlassBox
      className="tw-relative tw-h-[190px] tw-w-full tw-overflow-hidden"
      borderRadius="20px"
      status={event.status}
    >
      <div className="tw-flex tw-gap-4 tw-p-[20px]">
        <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-2 tw-text-white">
          <StatusBadge status={event.status} />
          <span className="tw-letter-spacing-[-0.032px] tw-line-clamp-1 tw-flex tw-items-center tw-gap-[6px] tw-text-left tw-font-jost tw-text-[16px] tw-font-bold tw-capitalize tw-leading-[120%]">
            {event.name}
          </span>
          <span className="tw-text-left tw-font-jost tw-text-[12px] tw-font-semibold tw-uppercase tw-text-[#20AA67]">
            {eventFormatDate(event)}
          </span>
          {hasParticipantCount && (
            <span className="tw-text-left tw-font-jost tw-text-[12px] tw-font-normal tw-capitalize tw-leading-[150%] tw-tracking-[-0.24px] tw-text-white">
              {eventFormatParticipantCount(event.participant_count)} Participants
            </span>
          )}
          <RewardsList rewards={event.rewards} className="tw-max-w-full" />
        </div>
        {hasImage && (
          <div className="tw-flex tw-shrink-0 tw-overflow-hidden">
            <img
              src={event.image_url!}
              alt={event.name}
              className="tw-h-[150px] tw-w-[150px] tw-rounded-[10px] tw-object-cover tw-object-right"
              onError={() => setImageError(true)}
            />
          </div>
        )}

        {hasVipBadge && (
          <div
            className="tw-absolute tw-bottom-0 tw-right-0 tw-flex tw-h-[34px] tw-w-[61px] tw-items-center tw-justify-center tw-gap-[8px] tw-rounded-tl-[20px] tw-bg-[#FFBD2D]"
            style={{
              filter: 'drop-shadow(0 0 6px rgba(0, 0, 0, 0.25))',
            }}
          >
            <span className="tw-font-jost tw-text-[12px] tw-font-semibold tw-uppercase tw-text-black">VIP</span>
          </div>
        )}
      </div>
    </CustomGlassBox>
  );

  if (isCustomEvent) {
    return (
      <button type="button" onClick={handleCustomEventClick} className={styles.eventCardLink}>
        {cardContent}
      </button>
    );
  }

  const link = getRouteForEventType(event.event_type, event.id.toString());
  return (
    <Link to={link} className={styles.eventCardLink}>
      {cardContent}
    </Link>
  );
};
